import { AqlVisionMark } from './aql-vision-brand';

interface AqlVisionLoaderProps {
  label?: string;
  detail?: string;
  className?: string;
  compact?: boolean;
}

export function AqlVisionLoader({
  label = 'A carregar dados...',
  detail,
  className = '',
  compact = false
}: AqlVisionLoaderProps) {
  const markSize = compact ? 'h-10 w-20' : 'h-16 w-32';
  const labelSpacing = compact ? 'mt-2' : 'mt-4';

  return (
    <div className={`flex flex-col items-center justify-center text-center ${className}`}>
      <style>
        {`
          @keyframes aqlVisionLoaderMorph {
            0%, 100% {
              transform: perspective(220px) rotateX(0deg) rotateY(0deg) skewX(0deg) scale(1);
              filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.28));
              opacity: 0.96;
            }
            28% {
              transform: perspective(220px) rotateX(5deg) rotateY(-8deg) skewX(-3deg) scaleX(0.94) scaleY(1.06);
              filter: drop-shadow(0 0 18px rgba(125, 211, 252, 0.5));
              opacity: 1;
            }
            58% {
              transform: perspective(220px) rotateX(-4deg) rotateY(7deg) skewX(4deg) scaleX(1.08) scaleY(0.92);
              filter: drop-shadow(0 0 14px rgba(34, 211, 238, 0.42));
              opacity: 0.9;
            }
            78% {
              transform: perspective(220px) rotateX(2deg) rotateY(-2deg) skewX(-1deg) scaleX(0.98) scaleY(1.02);
              filter: drop-shadow(0 0 16px rgba(186, 230, 253, 0.36));
              opacity: 1;
            }
          }

          @keyframes aqlVisionLoaderPath {
            0%, 100% {
              transform: translate3d(0, 0, 0) scale(1);
              opacity: 1;
            }
            35% {
              transform: translate3d(var(--aql-loader-x, 0), var(--aql-loader-y, 0), 0) scaleX(1.05) scaleY(0.94);
              opacity: 0.72;
            }
            68% {
              transform: translate3d(calc(var(--aql-loader-x, 0) * -0.72), calc(var(--aql-loader-y, 0) * 0.62), 0) scaleX(0.96) scaleY(1.07);
              opacity: 0.94;
            }
          }

          @keyframes aqlVisionLoaderEcho {
            0%, 100% {
              transform: scale(0.9);
              opacity: 0.1;
            }
            46% {
              transform: scale(1.18);
              opacity: 0.28;
            }
          }

          @keyframes aqlVisionLoaderScan {
            0% {
              transform: translateX(-55%) scaleX(0.4);
              opacity: 0;
            }
            36% {
              opacity: 0.72;
            }
            100% {
              transform: translateX(55%) scaleX(1);
              opacity: 0;
            }
          }

          .aql-vision-loader-mark {
            animation: aqlVisionLoaderMorph 2.8s ease-in-out infinite;
            transform-origin: center;
            will-change: transform, filter, opacity;
          }

          .aql-vision-loader-mark path {
            animation: aqlVisionLoaderPath 2.8s ease-in-out infinite;
            transform-box: fill-box;
            transform-origin: center;
            will-change: transform, opacity;
          }

          .aql-vision-loader-mark path:nth-child(1) { --aql-loader-x: -9px; --aql-loader-y: 4px; animation-delay: 0s; }
          .aql-vision-loader-mark path:nth-child(2) { --aql-loader-x: 9px; --aql-loader-y: -4px; animation-delay: 0.09s; }
          .aql-vision-loader-mark path:nth-child(3) { --aql-loader-x: -7px; --aql-loader-y: -6px; animation-delay: 0.18s; }
          .aql-vision-loader-mark path:nth-child(4) { --aql-loader-x: 8px; --aql-loader-y: 5px; animation-delay: 0.27s; }
          .aql-vision-loader-mark path:nth-child(5) { --aql-loader-x: -5px; --aql-loader-y: 7px; animation-delay: 0.36s; }
          .aql-vision-loader-mark path:nth-child(6) { --aql-loader-x: 6px; --aql-loader-y: -7px; animation-delay: 0.45s; }

          .aql-vision-loader-echo {
            animation: aqlVisionLoaderEcho 2.8s ease-in-out infinite;
            transform-origin: center;
            will-change: transform, opacity;
          }

          .aql-vision-loader-scan {
            animation: aqlVisionLoaderScan 1.7s ease-in-out infinite;
            will-change: transform, opacity;
          }

          @media (prefers-reduced-motion: reduce) {
            .aql-vision-loader-mark,
            .aql-vision-loader-mark path,
            .aql-vision-loader-echo,
            .aql-vision-loader-scan {
              animation: none;
            }
          }
        `}
      </style>

      <div className={`relative ${markSize}`} aria-hidden="true">
        <AqlVisionMark className="aql-vision-loader-echo absolute inset-0 h-full w-full text-sky-400/30 blur-[1px]" />
        <AqlVisionMark className="aql-vision-loader-mark relative h-full w-full text-sky-200" />
        <div className="aql-vision-loader-scan pointer-events-none absolute inset-x-2 bottom-1 h-px bg-gradient-to-r from-transparent via-cyan-200 to-transparent" />
      </div>

      {label ? <p className={`${labelSpacing} text-sm font-medium text-foreground`}>{label}</p> : null}
      {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}
